import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import { IoSearchOutline } from 'react-icons/io5';

import { getProducts } from '../../api/products';
import { getLinkSearchSeed, getSearchResultLine, isTypingTarget } from '../../helpers/links_to_check_flow_helpers';
import { useWindowKeydown } from '../../hooks/useWindowKeydown';
import SafeImage from '../PinMatchSilentEval/SafeImage';
import { useLinksToCheckContext } from './LinksToCheckContext';

import './LinkSearch.scss';

const RESULT_COUNT = 8;
const NUMBERED_RESULT_COUNT = 5;
const ARROW_STEPS = { ArrowDown: 1, ArrowUp: -1 };

/** Catalog search for the link on screen: the right side of a What is it? card, and the C pane on every other card. */
const LinkSearch = () => {
  const { link, pane, sheet, isKeysOpen, closePane, answer } = useLinksToCheckContext();
  const isWhatIsIt = link.question === 'what_is_it';
  const [query, setQuery] = useState(() => getLinkSearchSeed(link));
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [results, setResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(true);
  const inputRef = useRef(null);

  const linkResult = product => product && answer({ kind: 'link', product });
  const moveHighlight = step => setHighlightedIndex(index => _.clamp(index + step, 0, Math.max(results.length - 1, 0)));
  const leaveSearch = () => {
    inputRef.current?.blur();
    if (pane === 'search') closePane();
  };

  const moveOrLink = e => {
    if (ARROW_STEPS[e.key]) moveHighlight(ARROW_STEPS[e.key]);
    else if (e.key === 'Enter') linkResult(results[highlightedIndex]);
    else return;
    e.preventDefault();
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      leaveSearch();
    } else {
      moveOrLink(e);
    }
  };

  useEffect(() => {
    if (pane === 'search') inputRef.current?.focus();
  }, [pane]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.trim().length <= 2) {
      setResults([]);
      setIsSearching(false);
      return undefined;
    }
    let cancelled = false;
    setIsSearching(true);
    getProducts({ query: debouncedQuery, pageSize: RESULT_COUNT })
      .then(({ products }) => {
        if (cancelled) return;
        setResults(products || []);
        setHighlightedIndex(0);
      })
      .catch(() => {
        if (!cancelled) window.ALERT.error('Failed to search products. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Outside the field the results still take ↑↓ and ↵, Esc closes the pane, and on What is it? 1–5 link a result
  useWindowKeydown(e => {
    if (sheet || isKeysOpen || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
    if (e.key === 'Escape' && pane === 'search') return closePane();
    if (isWhatIsIt && /^[1-5]$/.test(e.key)) {
      e.preventDefault();
      linkResult(results[Number(e.key) - 1]);
    } else {
      moveOrLink(e);
    }
  });

  return (
    <div className='links-to-check-search'>
      <div className='side-label'>
        <span>Find it in the catalog</span>
        <span>{isWhatIsIt ? '1–5 to link · ↵ links the highlighted one' : '↑↓ and ↵ · Esc to go back'}</span>
      </div>
      <div className='search-box'>
        <IoSearchOutline />
        <input
          ref={inputRef}
          type='text'
          value={query}
          placeholder='Type what the page shows…'
          aria-label='Search the catalog'
          autoComplete='off'
          spellCheck={false}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleInputKeyDown}
        />
      </div>
      <div className='search-results'>
        {results.map((product, index) => (
          <button
            key={product.id}
            type='button'
            className={cn('search-result', { highlighted: index === highlightedIndex })}
            onClick={() => linkResult(product)}
          >
            <div className='search-result-photo'>
              <SafeImage className='ltc-photo-image' source={product.image} alt='' placeholderText='' />
            </div>
            <div className='search-result-text'>
              <div className='search-result-title'>{product.title}</div>
              <div className='search-result-meta'>{getSearchResultLine(product)}</div>
            </div>
            {isWhatIsIt && index < NUMBERED_RESULT_COUNT && <span className='search-result-key'>{index + 1}</span>}
          </button>
        ))}
        {!isSearching && !results.length && <div className='search-empty'>No matches yet. Try other words, or add it as new (N).</div>}
      </div>
      {isWhatIsIt && <div className='search-note'>Not there? Add it as new (N). We’ll fill it from the link.</div>}
    </div>
  );
};

export default LinkSearch;
